import React from 'react';

import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Tooltip from '@atlaskit/tooltip';

import { useExportTestCasesContext } from '@qality/common-components/src/lib/ExportTestCasesProvider';
import TestTypeDropdown from '@qality/common-components/src/molecules/TestTypeDropdown/TestTypeDropdown';
import { FEATURE_FLAG, IS_CLOUD } from '@qality/common-config';
import {
  StorageOfUniqueDialogs,
  StorageOfUniqueServices,
} from '@qality/common-service';
import { CreateTestCycleDialogRenderer } from '@qality/server-renderers';

import { useTestCaseRepositoryFoldersContext } from '../TestCaseRepositoryFoldersProvider';
import { useTestCaseRepositoryModalsContext } from '../TestCaseRepositoryModalsProvider';
import { useTestCaseRepositorySelectionContext } from '../TestCaseRepositorySelectionProvider';
import {
  BulkActionsWrapperStyled,
  BulkCheckActionsWrapperStyled,
  ContainerStyled,
  DropdownItemDeleteFromFolderStyled,
  SelectedIssuesCounterStyled,
} from './TestCaseRepositoryBulkBar.styled';
import { useTestCaseRepositoryBulkBarContext } from './TestCaseRepositoryBulkBarProvider';

const BULK_ACTIONS_QALITY_FREE_MESSAGE =
  'Test Case bulk actions available in QAlity Plus';

const EXPORT_QALITY_FREE_MESSAGE =
  'Exporting test cases available in QAlity Plus';

const REMOVE_ISSUES_FROM_SUBFOLDER_MESSAGE =
  'Open subfolder to bulk remove issues';

export default function TestCaseRepositoryBulkBar(): JSX.Element {
  const {
    allSelectedIssues,
    allSelectedIssuesIds,
    isAnyIssueSelected,
    clearAllSelectedIssues,
  } = useTestCaseRepositorySelectionContext();

  const {
    openAddToFolderModal,
    openCreateFolderModal,
    openDeleteFromFolderModal,
  } = useTestCaseRepositoryModalsContext();

  const { handleTestTypeSelect } = useTestCaseRepositoryBulkBarContext();

  const { currentView, currentFolder } = useTestCaseRepositoryFoldersContext();

  const { exportTestCases } = useExportTestCasesContext();

  const { numberOfSelectedIssuesInsideSubfolders } =
    useTestCaseRepositorySelectionContext();

  const selectedIssuesLabel = `${allSelectedIssuesIds.length} ${
    allSelectedIssuesIds.length === 1 ? 'Item' : 'Items'
  }`;

  const handleExport = async () => {
    exportTestCases({
      source: 'issues',
      issues: allSelectedIssues,
    });
  };

  const openAddToTestCycleDialog = async (): Promise<void> => {
    await StorageOfUniqueDialogs.get(
      'AddToTestCycleDialogService'
    ).openAddToTestCycleDialog(allSelectedIssuesIds);
  };

  const openCreateTestCycleDialog = () => {
    if (IS_CLOUD) {
      window.AP.dialog
        .create({
          key: 'create-test-cycle-dialog',
          size: 'maximum',
          chrome: false,
          customData: { isAddingTestCases: true },
        })
        .on('close', (data) => {
          addTestCasesToNewCycle(Number(data));
        });
    } else {
      CreateTestCycleDialogRenderer.openCreateTestCycleDialog({
        isAddingTestCases: true,
        onDialogClose: (data) => {
          addTestCasesToNewCycle(Number(data));
        },
      });
    }
  };

  const addTestCasesToNewCycle = async (testCycleId: number) => {
    if (testCycleId) {
      const testCyclesService = StorageOfUniqueServices.get(
        'TestCyclesServiceUnique'
      );

      await testCyclesService.addToCycle(allSelectedIssuesIds, testCycleId);
    }
  };

  const isDeleteFromFolderVisible =
    currentView === 'folder' && currentFolder.canEdit;

  const isDeleteFromFolderDisabled = numberOfSelectedIssuesInsideSubfolders > 0;

  if (!isAnyIssueSelected) {
    return null;
  }

  return (
    <ContainerStyled>
      <BulkCheckActionsWrapperStyled>
        <SelectedIssuesCounterStyled>
          {selectedIssuesLabel}
        </SelectedIssuesCounterStyled>
        <Button
          appearance='subtle-link'
          spacing='compact'
          onClick={clearAllSelectedIssues}
        >
          Uncheck all
        </Button>
      </BulkCheckActionsWrapperStyled>
      <BulkActionsWrapperStyled>
        {StorageOfUniqueServices.get('LicenseService').isAdvanced && (
          <TestTypeDropdown handleTestTypeSelection={handleTestTypeSelect} />
        )}
        <DropdownMenu trigger='Add to folder'>
          <DropdownItemGroup>
            <Tooltip
              position='left'
              content={
                !FEATURE_FLAG.isPremium && BULK_ACTIONS_QALITY_FREE_MESSAGE
              }
            >
              <DropdownItem
                isDisabled={!FEATURE_FLAG.isPremium}
                onClick={() =>
                  openAddToFolderModal({
                    testCasesIds: allSelectedIssuesIds,
                  })
                }
              >
                Choose folder
              </DropdownItem>
            </Tooltip>
            <Tooltip
              position='left'
              content={
                !FEATURE_FLAG.isPremium && BULK_ACTIONS_QALITY_FREE_MESSAGE
              }
            >
              <DropdownItem
                isDisabled={!FEATURE_FLAG.isPremium}
                onClick={() => {
                  openCreateFolderModal({
                    testCasesIds: allSelectedIssuesIds,
                  });
                }}
              >
                Create new folder
              </DropdownItem>
            </Tooltip>
          </DropdownItemGroup>
        </DropdownMenu>
        <DropdownMenu trigger='Add to test cycle'>
          <DropdownItemGroup>
            <Tooltip
              position='left'
              content={
                !FEATURE_FLAG.isPremium && BULK_ACTIONS_QALITY_FREE_MESSAGE
              }
            >
              <DropdownItem
                isDisabled={!FEATURE_FLAG.isPremium}
                onClick={openCreateTestCycleDialog}
              >
                Create new test cycle
              </DropdownItem>
            </Tooltip>
            <Tooltip
              position='left'
              content={
                !FEATURE_FLAG.isPremium && BULK_ACTIONS_QALITY_FREE_MESSAGE
              }
            >
              <DropdownItem
                isDisabled={!FEATURE_FLAG.isPremium}
                onClick={openAddToTestCycleDialog}
              >
                Choose test cycle
              </DropdownItem>
            </Tooltip>
          </DropdownItemGroup>
        </DropdownMenu>
        <DropdownMenu
          trigger={({ triggerRef, ...props }) => (
            <Button
              {...props}
              iconBefore={<MoreIcon label='more' />}
              ref={triggerRef}
            />
          )}
          placement='top-end'
        >
          <DropdownItemGroup>
            <Tooltip
              position='left'
              content={!FEATURE_FLAG.isPremium && EXPORT_QALITY_FREE_MESSAGE}
            >
              <DropdownItem
                onClick={handleExport}
                isDisabled={!FEATURE_FLAG.isPremium}
              >
                Export test cases
              </DropdownItem>
            </Tooltip>
            {isDeleteFromFolderVisible && (
              <Tooltip
                position='left'
                content={
                  isDeleteFromFolderDisabled &&
                  REMOVE_ISSUES_FROM_SUBFOLDER_MESSAGE
                }
              >
                <DropdownItem
                  key='add-to-folder'
                  onClick={() =>
                    openDeleteFromFolderModal({
                      testCasesInFolder: allSelectedIssues.map(
                        (issue) => issue.testCaseInFolder
                      ),
                      folder: currentFolder,
                    })
                  }
                  isDisabled={isDeleteFromFolderDisabled}
                >
                  <DropdownItemDeleteFromFolderStyled>
                    Remove from folder
                  </DropdownItemDeleteFromFolderStyled>
                </DropdownItem>
              </Tooltip>
            )}
          </DropdownItemGroup>
        </DropdownMenu>
      </BulkActionsWrapperStyled>
    </ContainerStyled>
  );
}
